<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[20000] flex flex-col"
      :style="backgroundStyle"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="hidden desktop:block absolute w-[1200px] h-[1200px] rounded-full"
        style="top: -200px; inset-inline-end: -700px; filter: blur(300px); background: rgba(255, 233, 200, 1)"
      ></div>
      <div
        class="hidden desktop:block absolute w-[1400px] h-[1400px] rounded-full"
        style="top: 300px; inset-inline-start: -900px; filter: blur(300px); background: rgba(255, 233, 200, 1)"
      ></div>
      <div
        class="hidden desktop:block absolute w-[1100px] h-[1100px] rounded-full"
        style="bottom: -300px; inset-inline-end: -200px; filter: blur(300px); background: rgba(255, 233, 200, 1)"
      ></div>

      <div
        class="desktop:hidden absolute w-[473px] h-[473px] rounded-full"
        style="top: -31px; inset-inline-start: 121px; filter: blur(200px); background: #ffe9c8; opacity: 0.9"
      ></div>
      <div
        class="desktop:hidden absolute w-[656px] h-[656px] rounded-full"
        style="
          top: 300px;
          inset-inline-start: -150px;
          filter: blur(250px);
          background: rgba(255, 219, 163, 1);
          opacity: 1;
        "
      ></div>
      <div
        class="desktop:hidden absolute w-[527px] h-[527px] rounded-full"
        style="
          top: 579px;
          inset-inline-start: 50px;
          filter: blur(200px);
          background: rgba(255, 219, 163, 1);
          opacity: 0.8;
        "
      ></div>

      <div
        ref="scrollContainerRef"
        :class="scrollContainerClasses"
        @click="handleBackdropClick"
        @scroll="handleScroll"
      >
        <button
          v-if="showCloseButton"
          type="button"
          class="absolute top-6 end-6 z-50 p-2 text-black text-2xl leading-none bg-transparent border-none cursor-pointer transition-opacity hover:opacity-60"
          :aria-label="t('learn.videoModal.close')"
          @click="closeModal"
        >
          ✕
        </button>

        <div
          class="relative w-full max-w-[1200px] mx-auto desktop:px-6 px-[14px] pt-16 pb-0 desktop:pb-[45px] desktop:pt-[61px] flex flex-col items-center"
        >
          <div class="text-center max-w-[800px] flex flex-col desktop:gap-2 px-4 desktop:px-0">
            <h1
              class="font-satoshi font-bold text-[20px] desktop:text-[42px] leading-[125%] tracking-[0%] text-center text-black"
            >
              {{ t('learn.upgradeModal.title') }}
            </h1>
            <h2
              class="font-satoshi font-bold text-[20px] desktop:text-[42px] leading-[125%] tracking-[0%] text-center text-gold-primary-120"
            >
              {{ t('learn.upgradeModal.subtitle') }}
            </h2>
            <p
              class="font-satoshi font-medium text-[15px] desktop:text-[24px] text-black-100 tracking-[0%] leading-[140%] text-center mt-4 mb-[30px] desktop:mb-[51px]"
            >
              {{ t('learn.upgradeModal.description') }}
            </p>
          </div>

          <div
            v-if="paywallVersion === 'v2'"
            class="v2-billing-tabs flex items-center justify-center desktop:bg-white bg-[#F8F7F3] w-full desktop:w-[472px] desktop:h-20 h-auto desktop:gap-[10px] gap-[10px] rounded-2xl desktop:p-2.5 p-[10px] desktop:mb-[32px] mb-[16px] desktop:border desktop:mx-auto"
            :class="[
              'desktop:static sticky top-0 z-50 border',
              isSticky && paywallVersion === 'v2'
                ? 'desktop:border-[rgba(255,182,72,1)] desktop:rounded-2xl desktop:bg-white border-none bg-[rgba(248,247,243,1)] -mx-[14px] rounded-none'
                : 'border-[rgba(255,182,72,1)]',
            ]"
            :style="stickyTabsStyle"
          >
            <button
              v-for="period in billingPeriods"
              :key="period.value"
              class="font-satoshi desktop:text-[20px] text-[15px] items-center justify-center desktop:flex block text-center leading-[140%] tracking-[0%] text-center flex items-center justify-center border-none cursor-pointer transition-all opacity-100 flex-1"
              :class="[
                activeBillingPeriod === period.value
                  ? 'bg-gold-primary-100 text-black-100 font-medium desktop:font-semibold desktop:h-[60px] h-[45px] rounded-[12px] p-0 desktop:p-4 flex gap-0'
                  : 'bg-transparent text-[#6E6F73] hover:text-black-100 font-medium desktop:h-[60px] h-[42px] desktop:p-4 p-2 gap-0 desktop:gap-2.5 rounded-xl',
              ]"
              @click="activeBillingPeriod = period.value"
            >
              <span class="flex flex-col items-center justify-center desktop:flex-row desktop:gap-2">
                <span
                  class="opacity-100 desktop:text-[20px] text-[15px]"
                  :class="activeBillingPeriod === period.value ? 'font-medium' : 'font-medium'"
                  >{{ period.label }}</span
                >
                <span
                  v-if="period.badge"
                  class="desktop:text-sm text-[11px] font-satoshi font-normal leading-[140%] opacity-100 whitespace-normal desktop:whitespace-nowrap"
                  :class="activeBillingPeriod === period.value ? 'text-black-100' : 'text-[#6E6F73]'"
                >
                  {{ t('learn.upgradeModal.saveBadge') }}
                </span>
              </span>
            </button>
          </div>

          <div
            v-else
            class="flex items-center justify-center desktop:bg-white bg-[#F8F7F3] w-full desktop:w-[472px] h-[45px] desktop:h-20 desktop:gap-[10px] gap-[10px] rounded-[16px] desktop:rounded-2xl p-[4px] desktop:p-2.5 mb-[12px] desktop:mb-[32px] desktop:mx-auto desktop:border border-[#FFB648] desktop:border-[#FFB648]"
            :class="[billingPeriods.length === 1 ? 'desktop:max-w-none' : '']"
          >
            <button
              v-for="period in billingPeriods"
              :key="period.value"
              class="font-satoshi desktop:text-[20px] text-[15px] items-center justify-center desktop:flex block text-center leading-[140%] tracking-[0%] border-none cursor-pointer transition-all opacity-100 flex-1 h-[37px] desktop:h-[60px] py-2 px-4 desktop:p-4 rounded-[12px]"
              :class="[
                activeBillingPeriod === period.value
                  ? 'bg-gold-primary-100 text-black-100 font-semibold desktop:font-semibold flex gap-0'
                  : 'bg-transparent text-[#6E6F73] hover:text-black-100 font-medium desktop:rounded-xl gap-0 desktop:gap-2.5',
                billingPeriods.length === 1 ? 'desktop:max-w-none' : '',
              ]"
              @click="activeBillingPeriod = period.value"
            >
              <span class="flex flex-row items-center justify-center gap-2 flex-nowrap whitespace-nowrap">
                <span
                  class="opacity-100 desktop:text-[20px] text-[15px]"
                  :class="activeBillingPeriod === period.value ? 'font-medium' : 'font-medium'"
                  >{{ period.label }}</span
                >
                <span
                  v-if="period.badge"
                  class="desktop:text-sm text-[11px] font-satoshi font-normal leading-[140%] opacity-100 whitespace-nowrap"
                  :class="activeBillingPeriod === period.value ? 'text-black-100' : 'text-[#6E6F73]'"
                >
                  {{ t('learn.upgradeModal.saveBadge') }}
                </span>
              </span>
            </button>
          </div>

          <div
            class="desktop:block hidden desktop:hidden flex flex-col w-full gap-[10px] desktop:px-4 px-6 relative z-10"
          >
            <button
              v-for="tier in packageTiers"
              :key="tier.value"
              class="font-satoshi font-normal text-[16px] leading-[140%] tracking-[0%] text-center flex items-center rounded-[12px] cursor-pointer transition-all w-full h-[50px] gap-[10px] relative overflow-hidden border"
              :class="[
                activeTier === tier.value ? 'text-white' : 'bg-transparent hover:opacity-80',
                tier.badge ? 'justify-between' : 'justify-start',
              ]"
              :style="
                activeTier === tier.value
                  ? `padding: 10px 20px; border-color: ${getTierBorderColor(tier.value)};`
                  : `padding: 10px 20px; border-color: ${getTierBorderColor(tier.value)}; color: ${getTierBorderColor(tier.value)};`
              "
              @click="activeTier = tier.value"
            >
              <div
                v-if="activeTier === tier.value"
                class="absolute inset-0"
                :style="getHeaderGradient(tier.value)"
              />
              <span class="relative z-10">{{ tier.label }}</span>
              <span
                v-if="tier.badge"
                class="relative z-10 text-xs font-satoshi font-semibold whitespace-nowrap bg-white text-gold-primary-120 px-2 py-1 rounded-full"
                >{{ t('learn.upgradeModal.mostPopular') }}</span
              >
            </button>
          </div>

          <div class="desktop:relative w-full desktop:px-0">
            <!-- Version 1: Expandable Features -->
            <div
              v-if="paywallVersion === 'v1'"
              class="grid grid-cols-1 desktop:grid-cols-2 w-full desktop:max-w-[820px] desktop:min-w-[124px] gap-[16px] desktop:gap-[25px] desktop:mx-auto desktop:auto-rows-fr desktop:px-0"
            >
              <TransitionGroup name="fade">
                <div
                  v-for="(pkg, pkgIndex) in filteredPackages"
                  :key="pkg.id"
                  class="flex flex-col"
                  @click.stop
                >
                  <div
                    class="flex flex-col transition-all duration-300 w-full relative desktop:top-0 overflow-visible rounded-[16px] desktop:rounded-[24px] desktop:bg-white desktop:border desktop:border-gray-200 desktop:px-6 desktop:pt-[24px] desktop:pb-[24px] desktop:p-0 desktop:shadow-[0px_6px_16px_0px_rgba(13,15,21,0.08)]"
                    :class="[
                      pkg.id === selectedPackageId
                        ? 'bg-[#FFF0DA] border-[1.5px] border-[#EDA436] shadow-[0px_6px_16px_rgba(13,15,21,0.08)] p-3 gap-2.5 desktop:bg-white desktop:border desktop:border-gray-200 desktop:shadow-[0px_6px_16px_0px_rgba(13,15,21,0.08)] desktop:p-0 desktop:px-6 desktop:pt-[24px] desktop:pb-[24px]'
                        : 'bg-white border-[1.5px] border-white p-3 gap-2.5 desktop:border desktop:border-gray-200 desktop:p-0 desktop:px-6 desktop:pt-[24px] desktop:pb-[24px]',
                    ]"
                    style="min-height: auto; align-self: flex-end"
                    @click="selectedPackageId = pkg.id"
                  >
                    <div class="relative z-10 flex flex-col h-full gap-2.5 desktop:gap-0">
                      <div class="desktop:hidden flex flex-row items-center justify-between gap-2">
                        <h3
                          class="font-satoshi font-bold text-[24px] leading-[140%] tracking-[0%] capitalize flex-1"
                          :style="`color: ${getTierBorderColor(pkg.tier || '')}`"
                        >
                          {{ t('learn.upgradeModal.planName', { tier: getTierDisplayName(pkg.tier || '') }) }}
                        </h3>
                        <button
                          type="button"
                          class="desktop:hidden w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-[1.5px] cursor-pointer transition-colors"
                          :class="
                            pkg.id === selectedPackageId
                              ? 'border-[#EDA436] bg-[#FFFFFF]'
                              : 'border-[#6E6F73] bg-transparent'
                          "
                          @click.stop="selectedPackageId = pkg.id"
                        >
                          <span
                            v-if="pkg.id === selectedPackageId"
                            class="w-2 h-2 rounded-full flex-shrink-0"
                            style="background: #ffb648"
                          />
                        </button>
                      </div>

                      <div class="desktop:flex hidden items-center justify-between desktop:pb-[16px]">
                        <h2
                          class="font-satoshi font-bold text-[24px] leading-[140%] tracking-[0%] capitalize"
                          :style="`color: ${getTierBorderColor(pkg.tier || '')}`"
                        >
                          {{ t('learn.upgradeModal.planName', { tier: getTierDisplayName(pkg.tier || '') }) }}
                        </h2>
                      </div>

                      <div
                        class="flex items-center justify-between desktop:pt-0 pt-0 desktop:pb-[32px] pb-[24px] desktop:ps-0 ps-0 gap-2"
                      >
                        <div class="flex items-baseline flex-none">
                          <span class="font-satoshi font-medium text-[36px] leading-[140%] tracking-[0%] text-black"
                            >${{ getPriceForPeriod(pkg) }}</span
                          >
                          <span class="text-lg text-gray-600 ms-1">/{{ periodLabelDisplay }}</span>
                        </div>
                        <span
                          v-if="isLastTier(pkg.tier)"
                          class="font-satoshi italic font-normal text-[14px] leading-[140%] text-end text-[#6E6F73] flex-none whitespace-nowrap"
                        >
                          *{{ t('learn.upgradeModal.limitedSeats') }}
                        </span>
                      </div>

                      <button
                        class="hidden desktop:flex font-satoshi w-full items-center justify-center font-bold text-base tracking-[0px] cursor-pointer transition-all rounded-xl px-4 desktop:mb-[24px] text-white border-none"
                        :disabled="isLoadingPackages || loadingPackageId === pkg.id"
                        :style="
                          pkgIndex === 0
                            ? `height: 48px; gap: 8px; line-height: 22px; background: linear-gradient(90deg, #CAA360 0%, #AD8640 100%);`
                            : `height: 48px; gap: 8px; line-height: 22px; background-color: ${getTierBorderColor(pkg.tier || '')};`
                        "
                        @click="handlePrimaryCta(pkg)"
                      >
                        {{ getPrimaryCtaText(pkg) }}
                      </button>

                      <button
                        v-if="paywallVersion !== 'v1'"
                        class="desktop:hidden font-satoshi w-full items-center justify-center font-bold text-base tracking-[0px] text-end cursor-pointer transition-all rounded-xl px-4 text-white mb-[32px]"
                        :class="expandedPackages[pkg.id] ? 'hidden' : 'flex'"
                        :disabled="isLoadingPackages || loadingPackageId === pkg.id"
                        :style="
                          pkgIndex === 0
                            ? `height: 48px; gap: 8px; border-width: 1.5px; line-height: 22px; background: linear-gradient(90deg, #CAA360 0%, #AD8640 100%); border-color: ${getTierBorderColor(pkg.tier || '')};`
                            : `height: 48px; gap: 8px; border-width: 1.5px; line-height: 22px; background-color: ${getTierBorderColor(pkg.tier || '')}; border-color: ${getTierBorderColor(pkg.tier || '')};`
                        "
                        @click="handlePrimaryCta(pkg)"
                      >
                        {{ getPrimaryCtaText(pkg) }}
                      </button>

                      <div
                        class="w-full flex flex-col items-stretch rounded-[12px] p-0 cursor-pointer transition-all mb-0 overflow-visible desktop:bg-[rgba(248,247,243,1)] desktop:px-4"
                        :class="[
                          expandedPackages[pkg.id] ? 'desktop:h-auto' : 'desktop:h-[48px]',
                          'h-[32px]',
                          pkg.id === selectedPackageId ? 'bg-white' : 'bg-[#F8F7F3]',
                        ]"
                        @click.stop="togglePackageExpansion(pkg.id, pkgIndex)"
                      >
                        <div class="flex flex-col items-start flex-1 min-h-0">
                          <div
                            class="flex items-center justify-between w-full h-[32px] desktop:h-[48px] flex-1 px-3 desktop:px-0"
                          >
                            <span
                              class="font-satoshi font-medium text-[13px] leading-[140%] text-[#6E6F73] flex items-center flex-1 desktop:text-sm desktop:font-normal desktop:text-black-60"
                              :class="expandedPackages[pkg.id] ? 'desktop:font-medium desktop:text-black-100' : ''"
                            >
                              {{
                                t('learn.upgradeModal.planFeaturesName', { tier: getTierDisplayName(pkg.tier || '') })
                              }}
                            </span>
                            <svg
                              class="w-6 h-6 flex-shrink-0 transition-transform duration-200 hidden desktop:block"
                              :class="expandedPackages[pkg.id] ? 'rotate-180' : ''"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clip-rule="evenodd"
                              />
                            </svg>
                            <svg
                              class="w-6 h-6 flex-shrink-0 desktop:hidden"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9 18L15 12L9 6"
                                stroke="#0D0F15"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </div>
                        </div>

                        <div
                          v-if="expandedPackages[pkg.id]"
                          class="hidden desktop:flex flex-col gap-4 pb-4 mt-4 overflow-visible"
                        >
                          <div
                            v-for="(feature, index) in pkg.features"
                            :key="feature.key"
                            class="flex items-start gap-[6px] relative w-full"
                            :class="feature.isPlaceholder ? 'hidden' : ''"
                          >
                            <div
                              v-if="!feature.isPlaceholder"
                              class="flex items-center justify-center w-[20px] h-[20px] flex-shrink-0 rounded-full mt-0.5"
                              :style="`background-color: ${getFeatureIconColor(pkg.tier || '')}`"
                            >
                              <svg
                                class="w-3.5 h-3.5 text-white"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M5 13l4 4L19 7"
                                  stroke="currentColor"
                                  stroke-width="3"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </div>
                            <div
                              v-else
                              class="w-[20px] h-[20px] flex-shrink-0"
                              aria-hidden="true"
                            />

                            <div
                              class="text-[16px] text-gray-800 leading-relaxed flex-1"
                              :class="index === pkg.features.length - 1 ? 'font-bold' : 'font-normal'"
                            >
                              <template v-if="feature.isPlaceholder">-</template>
                              <template v-else>{{ feature.name || feature.subtitle }}</template>
                            </div>

                            <div class="relative group flex-shrink-0">
                              <button
                                v-if="feature.hasInfo"
                                class="w-4 h-4 p-0 border-none bg-transparent cursor-pointer flex items-center justify-center flex-shrink-0"
                                type="button"
                                :aria-label="t('learn.upgradeModal.moreInfoAria')"
                              >
                                <svg
                                  class="w-4 h-4 group-hover:hidden"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M14.6668 7.99999C14.6668 4.31809 11.6821 1.33332 8.00016 1.33332C4.31826 1.33332 1.3335 4.31809 1.3335 7.99999C1.3335 11.6819 4.31826 14.6667 8.00016 14.6667C11.6821 14.6667 14.6668 11.6819 14.6668 7.99999Z"
                                    stroke="#B6B7B9"
                                  />
                                  <path
                                    d="M8.1613 11.3333V8.00001C8.1613 7.68574 8.1613 7.52861 8.06366 7.43097C7.96603 7.33334 7.8089 7.33334 7.49463 7.33334"
                                    stroke="#B6B7B9"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                  <path
                                    d="M7.99463 5.33334H8.00062"
                                    stroke="#B6B7B9"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>
                                <svg
                                  class="w-4 h-4 hidden group-hover:block"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M14.6668 7.99999C14.6668 4.31809 11.6821 1.33332 8.00016 1.33332C4.31826 1.33332 1.3335 4.31809 1.3335 7.99999C1.3335 11.6819 4.31826 14.6667 8.00016 14.6667C11.6821 14.6667 14.6668 11.6819 14.6668 7.99999Z"
                                    stroke="#FFB648"
                                  />
                                  <path
                                    d="M8.1613 11.3333V8.00001C8.1613 7.68574 8.1613 7.52861 8.06366 7.43097C7.96603 7.33334 7.8089 7.33334 7.49463 7.33334"
                                    stroke="#FFB648"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                  <path
                                    d="M7.99463 5.33334H8.00062"
                                    stroke="#FFB648"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>
                              </button>

                              <div
                                v-if="feature.hasInfo"
                                class="absolute z-[10000] w-auto max-w-[342px] start-[-350px] bottom-[-1.25rem] rounded-t-xl hidden group-hover:block"
                                style="
                                  background: rgba(248, 247, 243, 1);
                                  border-radius: 12px;
                                  border: 1px solid rgba(255, 182, 72, 1);
                                  padding: 16px;
                                  gap: 10px;
                                  box-shadow:
                                    0 4px 8px -1px rgba(0, 0, 0, 0.1),
                                    0 2px 4px -1px rgba(0, 0, 0, 0.06);
                                  white-space: normal;
                                "
                                @click.stop
                              >
                                <p
                                  class="font-satoshi font-normal text-[15px] leading-[136%] tracking-[0px] text-gray-800 whitespace-normal"
                                >
                                  {{ feature.description }}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TransitionGroup>
              <div class="desktop:hidden flex flex-col items-center w-full mt-4">
                <button
                  v-if="selectedPackage"
                  type="button"
                  class="font-satoshi w-full flex justify-center items-center gap-2 h-12 px-4 rounded-[12px] border-none cursor-pointer transition-all text-[#0D0F15] font-bold text-[16px] leading-[22px]"
                  style="background: #ffb648"
                  :disabled="isLoadingPackages || loadingPackageId === selectedPackage.id"
                  @click="handlePrimaryCta(selectedPackage)"
                >
                  {{ getPrimaryCtaText(selectedPackage) }}
                </button>
              </div>
            </div>

            <!-- Version 2: Always Visible Features -->
            <div
              v-else
              class="grid grid-cols-1 desktop:grid-cols-2 w-full desktop:max-w-[820px] desktop:min-w-[124px] gap-[16px] desktop:gap-[25px] desktop:mx-auto desktop:auto-rows-fr desktop:px-0"
            >
              <TransitionGroup name="fade">
                <div
                  v-for="(pkg, pkgIndex) in filteredPackages"
                  :key="pkg.id"
                  class="flex flex-col"
                  @click.stop
                >
                  <div
                    class="bg-white flex flex-col transition-all duration-300 w-full desktop:px-6 desktop:pt-[24px] desktop:pb-[24px] rounded-[16px] desktop:rounded-[24px] relative desktop:top-0 overflow-visible desktop:border desktop:border-gray-200 border-none p-[16px] desktop:p-0 desktop:px-6 desktop:pt-[24px] desktop:pb-[24px] shadow-[0px_6px_16px_0px_rgba(13,15,21,0.08)]"
                    style="min-height: auto; align-self: flex-end"
                    @click.stop
                  >
                    <div class="relative z-10 flex flex-col h-full">
                      <div class="desktop:hidden flex flex-row items-center justify-between mb-[24px] gap-2">
                        <h3
                          class="font-satoshi font-bold text-[36px] leading-[140%] tracking-[0%] capitalize"
                          :style="`color: ${getTierBorderColor(pkg.tier || '')}`"
                        >
                          {{ getTierDisplayName(pkg.tier || '') }}
                        </h3>
                      </div>

                      <div class="desktop:flex hidden items-center justify-between desktop:pb-[16px]">
                        <h2
                          class="font-satoshi font-bold text-[24px] leading-[140%] tracking-[0%] capitalize"
                          :style="`color: ${getTierBorderColor(pkg.tier || '')}`"
                        >
                          {{ t('learn.upgradeModal.planName', { tier: getTierDisplayName(pkg.tier || '') }) }}
                        </h2>
                        <span
                          v-if="isLastTier(pkg.tier)"
                          class="text-sm text-black-60 italic whitespace-nowrap"
                        >
                          *{{ t('learn.upgradeModal.limitedSeats') }}
                        </span>
                      </div>

                      <div
                        class="hidden desktop:block w-full border-t desktop:mt-2 desktop:mb-6 my-3"
                        style="border-color: rgba(255, 182, 72, 1)"
                      ></div>

                      <div class="flex items-baseline desktop:pt-0 pt-0 desktop:pb-[24px] pb-[20px] desktop:ps-0 ps-0">
                        <span class="font-satoshi font-medium text-[36px] leading-[140%] tracking-[0%] text-black"
                          >${{ getPriceForPeriod(pkg) }}</span
                        >
                        <span class="text-lg text-gray-600 ms-1">/{{ periodLabelDisplay }}</span>
                      </div>

                      <button
                        class="desktop:hidden font-satoshi w-full flex items-center justify-center font-bold text-base tracking-[0px] cursor-pointer transition-all rounded-xl px-4 text-white border-none mb-[24px]"
                        :disabled="isLoadingPackages || loadingPackageId === pkg.id"
                        :style="
                          pkgIndex === 0
                            ? `height: 48px; gap: 8px; line-height: 22px; background: linear-gradient(90deg, #CAA360 0%, #AD8640 100%);`
                            : `height: 48px; gap: 8px; line-height: 22px; background-color: ${getTierBorderColor(pkg.tier || '')};`
                        "
                        @click="handlePrimaryCta(pkg)"
                      >
                        {{ getPrimaryCtaText(pkg) }}
                      </button>

                      <div
                        class="font-satoshi font-medium text-[20px] leading-[140%] tracking-[0%] desktop:mb-4 mb-3 align-middle"
                        style="color: rgba(255, 182, 72, 1)"
                      >
                        {{ t('learn.upgradeModal.featuresName', { tier: getTierDisplayName(pkg.tier || '') }) }}
                      </div>

                      <div class="flex flex-col gap-3 desktop:mb-[32px] mb-[24px]">
                        <div
                          v-for="(feature, index) in pkg.features"
                          :key="feature.key"
                          class="flex items-start gap-[6px] relative w-full"
                          :class="feature.isPlaceholder ? 'desktop:flex hidden' : ''"
                        >
                          <div
                            v-if="!feature.isPlaceholder"
                            class="flex items-center justify-center w-[20px] h-[20px] flex-shrink-0 rounded-full mt-0.5"
                            :style="`background-color: ${getFeatureIconColor(pkg.tier || '')}`"
                          >
                            <svg
                              class="w-3.5 h-3.5 text-white"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M5 13l4 4L19 7"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </div>
                          <div
                            v-else
                            class="w-[20px] h-[20px] flex-shrink-0"
                            aria-hidden="true"
                          />

                          <div
                            class="text-[16px] text-gray-800 leading-relaxed flex-1"
                            :class="index === pkg.features.length - 1 ? 'font-bold' : 'font-normal'"
                          >
                            <template v-if="feature.isPlaceholder">-</template>
                            <template v-else>{{ feature.name || feature.subtitle }}</template>
                          </div>

                          <div class="relative group flex-shrink-0">
                            <button
                              v-if="feature.hasInfo"
                              class="w-4 h-4 p-0 border-none bg-transparent cursor-pointer flex items-center justify-center flex-shrink-0"
                              type="button"
                              :aria-label="t('learn.upgradeModal.moreInfoAria')"
                              @click="activeInfoModal = feature.key"
                            >
                              <svg
                                class="w-4 h-4 desktop:group-hover:hidden"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M14.6668 7.99999C14.6668 4.31809 11.6821 1.33332 8.00016 1.33332C4.31826 1.33332 1.3335 4.31809 1.3335 7.99999C1.3335 11.6819 4.31826 14.6667 8.00016 14.6667C11.6821 14.6667 14.6668 11.6819 14.6668 7.99999Z"
                                  stroke="#B6B7B9"
                                />
                                <path
                                  d="M8.1613 11.3333V8.00001C8.1613 7.68574 8.1613 7.52861 8.06366 7.43097C7.96603 7.33334 7.8089 7.33334 7.49463 7.33334"
                                  stroke="#B6B7B9"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                                <path
                                  d="M7.99463 5.33334H8.00062"
                                  stroke="#B6B7B9"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                              <svg
                                class="w-4 h-4 hidden desktop:group-hover:block"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M14.6668 7.99999C14.6668 4.31809 11.6821 1.33332 8.00016 1.33332C4.31826 1.33332 1.3335 4.31809 1.3335 7.99999C1.3335 11.6819 4.31826 14.6667 8.00016 14.6667C11.6821 14.6667 14.6668 11.6819 14.6668 7.99999Z"
                                  stroke="#FFB648"
                                />
                                <path
                                  d="M8.1613 11.3333V8.00001C8.1613 7.68574 8.1613 7.52861 8.06366 7.43097C7.96603 7.33334 7.8089 7.33334 7.49463 7.33334"
                                  stroke="#FFB648"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                                <path
                                  d="M7.99463 5.33334H8.00062"
                                  stroke="#FFB648"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </button>

                            <div
                              v-if="feature.hasInfo"
                              class="absolute z-[10000] w-auto max-w-[342px] start-[-350px] bottom-[-1.25rem] rounded-t-xl hidden desktop:group-hover:block"
                              style="
                                background: rgba(248, 247, 243, 1);
                                border-radius: 12px;
                                border: 1px solid rgba(255, 182, 72, 1);
                                padding: 16px;
                                box-shadow: 0px -6px 16px 0px rgba(13, 15, 21, 0.08);
                                white-space: normal;
                              "
                              @click.stop
                            >
                              <p
                                class="font-satoshi font-normal text-[15px] leading-[136%] tracking-[0px] text-gray-800 whitespace-normal"
                              >
                                {{ feature.description }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        class="hidden desktop:flex font-satoshi w-full items-center justify-center font-bold text-base tracking-[0px] cursor-pointer transition-all rounded-xl px-4 text-white border-none"
                        :disabled="isLoadingPackages || loadingPackageId === pkg.id"
                        :style="
                          pkgIndex === 0
                            ? `height: 48px; gap: 8px; line-height: 22px; background: linear-gradient(90deg, #CAA360 0%, #AD8640 100%);`
                            : `height: 48px; gap: 8px; line-height: 22px; background-color: ${getTierBorderColor(pkg.tier || '')};`
                        "
                        @click="handlePrimaryCta(pkg)"
                      >
                        {{ getPrimaryCtaText(pkg) }}
                      </button>

                      <div
                        v-if="isLastTier(pkg.tier)"
                        class="desktop:hidden text-center mt-3"
                      >
                        <span class="text-sm text-black-60 italic">*{{ t('learn.upgradeModal.limitedSeats') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </TransitionGroup>
            </div>
          </div>

          <!-- Compare Plans Link - Version 1 Only -->
          <div
            v-if="paywallVersion === 'v1'"
            class="w-full flex items-center justify-center desktop:mt-[38px] mt-[40px] desktop:mb-[51px]"
          >
            <button
              class="font-satoshi font-medium text-[16px] leading-[140%] tracking-[0%] text-center underline decoration-solid underline-offset-[4px] decoration-[1px] cursor-pointer bg-transparent border-none mb-[51px] p-0"
              @click="showComparePlans = true"
            >
              {{ t('learn.upgradeModal.comparePlans') }}
            </button>
          </div>

          <!-- Mobile slider modals - teleported to body -->
          <template
            v-for="(pkg, pkgIndex) in filteredPackages"
            :key="`modal-${pkg.id}`"
          >
            <Teleport to="body">
              <div
                v-if="expandedPackages[pkg.id]"
                class="desktop:hidden fixed inset-0 flex items-end justify-center bg-black/50 backdrop-blur-sm"
                style="z-index: 9999999 !important"
                @click="togglePackageExpansion(pkg.id)"
              >
                <div
                  class="w-full bg-white rounded-t-[24px] relative flex flex-col touch-pan-x shadow-[0px_6px_16px_0px_rgba(13,15,21,0.08)] h-[588px] p-[32px_24px_51px_24px]"
                  @click.stop
                  @touchstart="
                    (e) => {
                      const touch = e.touches[0];
                      (e.currentTarget as any).__startX = touch.clientX;
                    }
                  "
                  @touchend="
                    (e) => {
                      const touch = e.changedTouches[0];
                      const startX = (e.currentTarget as any).__startX;
                      const diffX = touch.clientX - startX;
                      if (Math.abs(diffX) > 50) {
                        handleSwipe(diffX > 0 ? 'right' : 'left');
                      }
                    }
                  "
                >
                  <div class="flex flex-col items-center justify-center w-full flex-shrink-0">
                    <div
                      class="flex items-center text-start justify-left font-satoshi font-bold text-white mb-2 w-full"
                      :style="`height: 67px; border-radius: 12px; padding: 10px 20px; background-color: ${getTierBorderColor(pkg.tier || '')}; font-size: 24px; line-height: 140%; letter-spacing: 0%; text-align: start;`"
                    >
                      {{ t('learn.upgradeModal.planName', { tier: getTierDisplayName(pkg.tier || '') }) }}
                    </div>
                  </div>
                  <div class="flex-1 overflow-y-auto pt-8 pb-[51px] ms-1">
                    <div class="flex flex-col gap-4">
                      <div
                        v-for="(feature, index) in pkg.features"
                        :key="feature.key"
                        class="flex items-start gap-[6px] relative w-full"
                        :class="feature.isPlaceholder ? 'hidden' : ''"
                      >
                        <div
                          v-if="!feature.isPlaceholder"
                          class="flex items-center justify-center w-[20px] h-[20px] flex-shrink-0 rounded-full mt-0.5"
                          :style="`background-color: ${getFeatureIconColor(pkg.tier || '')}`"
                        >
                          <svg
                            class="w-3.5 h-3.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M5 13l4 4L19 7"
                              stroke="currentColor"
                              stroke-width="3"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </div>
                        <div
                          v-else
                          class="w-[20px] h-[20px] flex-shrink-0"
                          aria-hidden="true"
                        />

                        <div
                          class="text-[16px] text-gray-800 leading-relaxed flex-1"
                          :class="index === pkg.features.length - 1 ? 'font-bold' : 'font-normal'"
                        >
                          <template v-if="feature.isPlaceholder">-</template>
                          <template v-else>{{ feature.name || feature.subtitle }}</template>
                        </div>

                        <div class="relative group flex-shrink-0">
                          <button
                            v-if="feature.hasInfo"
                            class="w-4 h-4 p-0 border-none bg-transparent cursor-pointer flex items-center justify-center flex-shrink-0"
                            type="button"
                            :aria-label="t('learn.upgradeModal.moreInfoAria')"
                            @click="activeInfoModal = feature.key"
                          >
                            <svg
                              class="w-4 h-4"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M14.6668 7.99999C14.6668 4.31809 11.6821 1.33332 8.00016 1.33332C4.31826 1.33332 1.3335 4.31809 1.3335 7.99999C1.3335 11.6819 4.31826 14.6667 8.00016 14.6667C11.6821 14.6667 14.6668 11.6819 14.6668 7.99999Z"
                                stroke="#B6B7B9"
                              />
                              <path
                                d="M8.1613 11.3333V8.00001C8.1613 7.68574 8.1613 7.52861 8.06366 7.43097C7.96603 7.33334 7.8089 7.33334 7.49463 7.33334"
                                stroke="#B6B7B9"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                              <path
                                d="M7.99463 5.33334H8.00062"
                                stroke="#B6B7B9"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex-shrink-0 flex flex-col items-center justify-center bg-white pb-4">
                    <button
                      class="font-satoshi w-full items-center justify-center font-bold text-base tracking-[0px] cursor-pointer transition-all rounded-xl px-4 text-white mb-4"
                      :disabled="isLoadingPackages || loadingPackageId === pkg.id"
                      :style="
                        pkgIndex === 0
                          ? `height: 48px; gap: 8px; line-height: 22px; background: linear-gradient(90deg, #CAA360 0%, #AD8640 100%);`
                          : `height: 48px; gap: 8px; line-height: 22px; background-color: ${getTierBorderColor(pkg.tier || '')};`
                      "
                      @click="handlePrimaryCta(pkg)"
                    >
                      {{ getPrimaryCtaText(pkg) }}
                    </button>
                    <button
                      class="font-satoshi font-medium text-[16px] leading-[140%] tracking-[0%] text-center mb-6 underline decoration-solid underline-offset-[4px] decoration-[1px] cursor-pointer bg-transparent border-none p-0 mb-4 text-[#6E6F73]"
                      @click="
                        showComparePlans = true;
                        expandedPackages[pkg.id] = false;
                      "
                    >
                      {{ t('learn.upgradeModal.comparePlans') }}
                    </button>
                    <div class="flex items-center justify-center">
                      <img
                        :src="`/icons/slider-indicator-${activeSlideIndex + 1}.svg`"
                        :alt="t('learn.upgradeModal.sliderIndicatorAlt')"
                        class="block w-[23px] h-[6px] min-w-[23px] min-h-[6px]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Teleport>
          </template>

          <!-- Info Modal for mobile features -->
          <Teleport to="body">
            <div
              v-if="activeInfoModal"
              class="desktop:hidden fixed inset-0 flex items-end justify-center"
              style="z-index: 99999999 !important"
              @click="activeInfoModal = null"
            >
              <div
                :class="[
                  'w-full relative flex flex-col max-h-[60vh]',
                  paywallVersion === 'v2' ? 'text-start' : 'text-center',
                ]"
                style="
                  background: rgba(248, 247, 243, 1);
                  box-shadow: 0px -6px 16px 0px rgba(13, 15, 21, 0.08);
                  padding: 16px;
                "
                @click.stop
              >
                <div class="flex-1 overflow-y-auto">
                  <p class="font-satoshi font-normal text-[16px] leading-[140%] text-gray-800">
                    {{
                      filteredPackages.flatMap((p) => p.features).find((f) => f.key === activeInfoModal)?.description
                    }}
                  </p>
                </div>
              </div>
            </div>
          </Teleport>

          <!-- Compare Plans Modal -->
          <Teleport to="body">
            <div
              v-if="showComparePlans"
              class="fixed inset-0 flex desktop:items-center items-end justify-center bg-black/50 backdrop-blur-sm"
              style="z-index: 99999999 !important"
              @click="showComparePlans = false"
            >
              <div
                class="bg-white desktop:rounded-[24px] rounded-t-[24px] relative flex flex-col desktop:w-[768px] w-full desktop:max-h-[80vh] max-h-[90vh] desktop:p-10 p-6 shadow-[0px_6px_16px_0px_rgba(13,15,21,0.08)]"
                @click.stop
              >
                <div class="flex items-center justify-between desktop:mb-[37px] mb-[16px]">
                  <h2 class="font-satoshi font-bold text-[24px] leading-[140%] text-black">
                    {{ t('learn.upgradeModal.comparePlans') }}
                  </h2>
                  <button
                    class="desktop:flex hidden w-8 h-8 p-0 border-none bg-transparent cursor-pointer items-center justify-center"
                    @click="showComparePlans = false"
                  >
                    <svg
                      class="w-6 h-6"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        d="M18 6L6 18M6 6l12 12"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                <div class="flex gap-1 mb-8">
                  <div
                    v-for="(pkg, index) in filteredPackages"
                    :key="pkg.id"
                    class="flex-1 font-satoshi font-bold text-[16px] leading-[140%] py-3 px-4 transition-all text-white border-none flex items-center justify-left"
                    :class="index === 0 ? 'rounded-s-[12px] rounded-e-none' : 'rounded-e-[12px] rounded-s-none'"
                    :style="`background-color: ${getTierBorderColor(pkg.tier || '')}; height: 42px;`"
                  >
                    {{ t('learn.upgradeModal.planName', { tier: getTierDisplayName(pkg.tier || '') }) }}
                  </div>
                </div>

                <div class="flex-1 overflow-y-auto">
                  <div class="flex flex-col gap-4 px-1">
                    <div
                      v-for="(feature, fIndex) in compareFeatureRows"
                      :key="fIndex"
                      class="flex flex-col gap-2"
                    >
                      <div class="font-satoshi font-semibold text-[14px] leading-[140%] text-black ms-2">
                        {{ feature.label }}
                      </div>

                      <div class="grid grid-cols-2 gap-1">
                        <div
                          v-for="(pkg, pkgIndex) in filteredPackages"
                          :key="pkg.id"
                          class="flex items-center justify-center py-3 max-h-[28px]"
                          :style="`background-color: ${
                            typeof feature[pkgIndex === 0 ? 'investor' : 'syndicate'] === 'boolean' &&
                            !feature[pkgIndex === 0 ? 'investor' : 'syndicate']
                              ? 'rgba(248, 248, 248, 1)'
                              : activeSlideIndex === pkgIndex
                                ? 'rgba(255, 240, 218, 1)'
                                : 'rgba(243, 243, 243, 1)'
                          };`"
                        >
                          <template v-if="typeof feature[pkgIndex === 0 ? 'investor' : 'syndicate'] === 'boolean'">
                            <svg
                              v-if="feature[pkgIndex === 0 ? 'investor' : 'syndicate']"
                              class="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                fill="#10B981"
                              />
                              <path
                                d="M8 12l3 3 5-5"
                                stroke="white"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                            <img
                              v-else
                              src="/icons/circle-minus.svg"
                              :alt="t('learn.upgradeModal.notIncludedAlt')"
                              class="w-4 h-4"
                            />
                          </template>
                          <template v-else>
                            <span class="font-satoshi font-normal text-[14px] leading-[140%] text-black-100">
                              {{ feature[pkgIndex === 0 ? 'investor' : 'syndicate'] }}
                            </span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Teleport>

          <div
            v-if="paywallVersion === 'v1'"
            class="w-full max-w-[1248px] mx-auto grid grid-cols-1 desktop:grid-cols-3 gap-[25px] desktop:-mt-10 desktop:px-0"
          >
            <div
              class="flex flex-col desktop:w-[399px] desktop:flex-row desktop:items-center items-center justify-center bg-white rounded-[16px] desktop:rounded-[32px] desktop:py-3 desktop:h-[65px] mt-0 desktop:mt-[45px] w-full desktop:w-[399px] py-[10px] px-[24px] desktop:px-6 gap-[6.18px] desktop:gap-4 mx-auto"
            >
              <div class="flex items-center">
                <div
                  v-for="i in 4"
                  :key="i"
                  class="w-[40px] h-[40px] rounded-full border flex-shrink-0 relative bg-gold-primary-100"
                  :class="i === 1 ? '' : '-ms-3'"
                  :style="`z-index: ${5 - i}`"
                >
                  <img
                    :src="getAvatarPath(i)"
                    :alt="t('learn.upgradeModal.userAvatarAlt', { index: i })"
                    class="w-full h-full object-cover rounded-full"
                  />
                  <div
                    class="absolute bottom-0.5 end-0.5 w-[7px] h-[7px] rounded-full border border-white z-10"
                    :class="i === 3 ? 'bg-red-500' : 'bg-green-500'"
                  />
                </div>
                <div
                  class="w-[40px] h-[40px] rounded-full border bg-gold-primary-100 text-white flex items-center justify-center font-satoshi font-bold text-[9px] -ms-3 ps-1 z-0"
                >
                  1.2k
                </div>
              </div>
              <p
                class="font-satoshi text-sm font-normal leading-[140%] text-black-100 text-center desktop:text-start desktop:pe-[6px] pe-0"
              >
                {{ t('learn.upgradeModal.investorsSecuring') }}
              </p>
            </div>
          </div>

          <div
            v-else
            class="w-full desktop:max-w-[820px] mx-auto desktop:px-0 desktop:mt-[55px] mt-[21px]"
          >
            <div
              class="flex flex-col desktop:w-[399px] desktop:flex-row desktop:items-center items-center justify-center bg-white rounded-[16px] desktop:rounded-[32px] desktop:py-3 desktop:h-[65px] w-full desktop:w-[399px] py-[10px] px-[24px] desktop:px-6 gap-[6.18px] desktop:gap-4"
            >
              <div class="flex items-center">
                <div
                  v-for="i in 4"
                  :key="i"
                  class="w-[40px] h-[40px] rounded-full border flex-shrink-0 relative bg-gold-primary-100"
                  :class="i === 1 ? '' : '-ms-3'"
                  :style="`z-index: ${5 - i}`"
                >
                  <img
                    :src="getAvatarPath(i)"
                    :alt="t('learn.upgradeModal.userAvatarAlt', { index: i })"
                    class="w-full h-full object-cover rounded-full"
                  />
                  <div
                    class="absolute bottom-0.5 end-0.5 w-[7px] h-[7px] rounded-full border border-white z-10"
                    :class="i === 3 ? 'bg-red-500' : 'bg-green-500'"
                  />
                </div>
                <div
                  class="w-[40px] h-[40px] rounded-full border bg-gold-primary-100 text-white flex items-center justify-center font-satoshi font-bold text-[9px] -ms-3 ps-1 z-0"
                >
                  1.2k
                </div>
              </div>
              <p
                class="font-satoshi text-sm font-normal leading-[140%] text-black-100 text-center desktop:text-start desktop:pe-[6px] pe-0"
              >
                {{ t('learn.upgradeModal.investorsSecuring') }}
              </p>
            </div>
          </div>

          <div class="w-full bg-transparent pt-0 pb-6 desktop:py-0">
            <PartnersSlider class="w-full px-4 desktop:px-0" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { Package } from '~/api/queries/packages';
import { usePackages } from '~/api/queries/packages';
import { POPUPS } from '@/components/dynamic/maps';
import useBodyScrollLock from '~/composables/useBodyScrollLock';
import { isValidTier } from '@/constants/tier-features';

const { t } = useI18n();

const FEATURE_ROWS = 8;

const PERIOD_TAB_LABEL_KEYS: Record<string, string> = {
  monthly: 'learn.upgradeModal.periodTabMonthly',
  yearly: 'learn.upgradeModal.periodTabYearly',
  lifetime: 'learn.upgradeModal.periodTabLifetime',
};

const periodTabLabel = (period: string): string => {
  const key = PERIOD_TAB_LABEL_KEYS[period.toLowerCase()];
  return key ? t(key) : period.charAt(0).toUpperCase() + period.slice(1);
};

const TIER_TAB_LABEL_KEYS: Record<string, string> = {
  scout: 'learn.upgradeModal.tierTabScout',
  investor: 'learn.upgradeModal.tierTabInvestor',
  syndicate: 'learn.upgradeModal.tierTabSyndicate',
};

const tierTabLabel = (tier: string): string => {
  const key = TIER_TAB_LABEL_KEYS[tier.toLowerCase()];
  return key ? t(key) : tier.charAt(0).toUpperCase() + tier.slice(1);
};

const modelValue = defineModel<boolean>({ default: false });

const props = defineProps<{
  initialTier?: string;
  version?: 'v1' | 'v2';
}>();

defineEmits<{
  upgrade: [];
  seeMore: [];
}>();

const authStore = useAuthStore();
const route = useRoute();
const { sendEventToBackend } = useTracking();
const { getPaywallVersion } = usePaywallParams();
const config = useRuntimeConfig();

const paywallVersion = computed(() => {
  if (props.version) {
    return props.version;
  }
  if (authStore.user?.paywallVersion) {
    return authStore.user.paywallVersion === 'v2' ? 'v2' : 'v1';
  }
  return getPaywallVersion();
});

const isSticky = ref(false);
const scrollContainerRef = ref<HTMLElement | null>(null);
// Default to a desktop-ish width so SSR and the first client paint match.
// handleResize() updates this in onMounted on the client.
const windowWidth = ref(1024);

const backgroundStyle = computed(() => {
  return { background: 'white' };
});

const handleResize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth;
  }
};

const stickyTabsStyle = computed(() => {
  if (typeof window !== 'undefined' && isSticky.value && paywallVersion.value === 'v2') {
    const isDesktop = window.innerWidth >= 1024;
    if (!isDesktop) {
      return 'box-shadow: 0px 6px 16px 0px rgba(13, 15, 21, 0.08); padding: 8px 14px; width: calc(100% + 28px);';
    }
  }
  return '';
});

const handleScroll = (e: Event) => {
  const target = e.target as HTMLElement;
  if (target && paywallVersion.value === 'v2' && typeof window !== 'undefined') {
    const isDesktop = window.innerWidth >= 1024;
    if (!isDesktop) {
      // Get the tabs element position relative to the scroll container
      const tabsElement = document.querySelector('.v2-billing-tabs');
      if (tabsElement) {
        const rect = tabsElement.getBoundingClientRect();
        // Make sticky when tabs reach the top of the viewport
        isSticky.value = rect.top <= 0;
      }
    }
  }
};

onMounted(() => {
  if (typeof window === 'undefined') return;

  isSafari.value = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
});

const isPaywallForced = computed(() => {
  if (authStore.user?.paywallForce === true) return true;
  if (authStore.user?.paywallForce === false) return false;
  if (!route?.query) return true;
  const paywallForce = route.query.paywall_force;
  if (paywallForce === undefined || paywallForce === null) return true;
  if (Array.isArray(paywallForce)) return !paywallForce.some((v) => String(v).toLowerCase().trim() === 'false');
  return String(paywallForce).toLowerCase().trim() !== 'false';
});

const showCloseButton = computed(() => {
  return !isPaywallForced.value;
});

const { data: packagesData, isLoading: isLoadingPackages } = usePackages();

const allPackages = computed(() => {
  if (!packagesData.value?.data || !Array.isArray(packagesData.value.data)) return [];
  return packagesData.value.data as Package[];
});

const billingPeriods = computed(() => {
  const periods = new Set<string>();
  allPackages.value.forEach((pkg) => {
    if (pkg.billingPeriod) {
      periods.add(pkg.billingPeriod);
    }
  });

  const periodOrder = ['monthly', 'yearly', 'lifetime'];
  const sortedPeriods = Array.from(periods).sort((a, b) => {
    const aIdx = periodOrder.indexOf(a.toLowerCase());
    const bIdx = periodOrder.indexOf(b.toLowerCase());
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return a.localeCompare(b);
  });

  return sortedPeriods.map((period) => {
    const periodLower = period.toLowerCase();
    return {
      value: period,
      label: periodTabLabel(period),
      badge: periodLower === 'yearly',
    };
  });
});

const packageTiers = computed(() => {
  const seenTiers = new Set<string>();
  const tiersInOrder: string[] = [];

  allPackages.value.forEach((pkg) => {
    if (pkg.tier && !seenTiers.has(pkg.tier)) {
      seenTiers.add(pkg.tier);
      tiersInOrder.push(pkg.tier);
    }
  });

  let maxUserCount = 0;
  let mostPopularTier = '';

  tiersInOrder.forEach((tier) => {
    const tierPackages = allPackages.value.filter((p) => p.tier === tier);
    const totalUsers = tierPackages.reduce((sum, p) => sum + (p.userCount || 0), 0);
    if (totalUsers > maxUserCount) {
      maxUserCount = totalUsers;
      mostPopularTier = tier;
    }
  });

  return tiersInOrder.map((tier) => ({
    value: tier,
    label: tierTabLabel(tier),
    badge: tier === mostPopularTier && maxUserCount > 0,
  }));
});

// Compare-plans modal rows — static copy (not backend data). `investor` /
// `syndicate` hold either a translated display string or a boolean (booleans
// render as check/minus icons in the template, never as text).
const compareFeatureRows = computed(() => [
  {
    label: t('learn.upgradeModal.compareFeatures.satisfactionGuaranteeLabel'),
    key: 'satisfaction',
    investor: t('learn.upgradeModal.compareFeatures.satisfactionGuaranteeInvestor'),
    syndicate: t('learn.upgradeModal.compareFeatures.satisfactionGuaranteeSyndicate'),
  },
  {
    label: t('learn.upgradeModal.compareFeatures.portfolioManagementLabel'),
    key: 'portfolio',
    investor: t('learn.upgradeModal.compareFeatures.portfolioManagementInvestor'),
    syndicate: t('learn.upgradeModal.compareFeatures.portfolioManagementSyndicate'),
  },
  {
    label: t('learn.upgradeModal.compareFeatures.advisorAccessLabel'),
    key: 'advisor',
    investor: t('learn.upgradeModal.compareFeatures.advisorAccessInvestor'),
    syndicate: t('learn.upgradeModal.compareFeatures.advisorAccessSyndicate'),
  },
  {
    label: t('learn.upgradeModal.compareFeatures.prelaunchAccessLabel'),
    key: 'prelaunch',
    investor: true,
    syndicate: true,
  },
  {
    label: t('learn.upgradeModal.compareFeatures.groupBuysLabel'),
    key: 'groupbuys',
    investor: true,
    syndicate: true,
  },
  {
    label: t('learn.upgradeModal.compareFeatures.priorityLabel'),
    key: 'priority',
    investor: false,
    syndicate: true,
  },
  {
    label: t('learn.upgradeModal.compareFeatures.hiddenInventoryLabel'),
    key: 'hidden',
    investor: false,
    syndicate: true,
  },
  {
    label: t('learn.upgradeModal.compareFeatures.conciergeLabel'),
    key: 'concierge',
    investor: false,
    syndicate: true,
  },
]);

const defaultBillingPeriod = computed(() => {
  return billingPeriods.value[0]?.value || 'monthly';
});

const defaultTier = computed(() => {
  if (props.initialTier) {
    const initialTierExists = packageTiers.value.find(
      (t) => t.value.toLowerCase() === props.initialTier?.toLowerCase(),
    );
    if (initialTierExists) return initialTierExists.value;
  }

  const investorExists = packageTiers.value.find((t) => t.value.toLowerCase() === 'investor');
  if (investorExists) return investorExists.value;

  const mostPopular = packageTiers.value.find((t) => t.badge === true);
  return mostPopular ? mostPopular.value : packageTiers.value[0]?.value || 'scout';
});

const activeBillingPeriod = ref(defaultBillingPeriod.value);
const activeTier = ref(defaultTier.value);
const expandedPackages = ref<Record<string, boolean>>({});
const selectedPackageId = ref<string | null>(null);
const activeSlideIndex = ref(0);
const activeInfoModal = ref<string | null>(null);
const showComparePlans = ref(false);

watch([defaultBillingPeriod, defaultTier], ([newBillingPeriod, newTier]) => {
  if (!activeBillingPeriod.value) activeBillingPeriod.value = newBillingPeriod;
  if (!activeTier.value) activeTier.value = newTier;
});

watch(
  [activeBillingPeriod, allPackages],
  () => {
    const hasPackagesForPeriod = allPackages.value.some(
      (pkg) => pkg?.billingPeriod && pkg.billingPeriod === activeBillingPeriod.value,
    );
    if (!hasPackagesForPeriod && billingPeriods.value.length > 0) {
      activeBillingPeriod.value = defaultBillingPeriod.value;
    }
  },
  { immediate: true },
);

const togglePackageExpansion = (packageId: string, index?: number) => {
  expandedPackages.value[packageId] = !expandedPackages.value[packageId];
  if (typeof index === 'number') {
    activeSlideIndex.value = index;
  }
};

const handleSwipe = (direction: 'left' | 'right') => {
  const packages = filteredPackages.value;
  if (direction === 'left' && activeSlideIndex.value < packages.length - 1) {
    // Close current
    expandedPackages.value[packages[activeSlideIndex.value].id] = false;
    // Open next
    activeSlideIndex.value++;
    expandedPackages.value[packages[activeSlideIndex.value].id] = true;
  } else if (direction === 'right' && activeSlideIndex.value > 0) {
    // Close current
    expandedPackages.value[packages[activeSlideIndex.value].id] = false;
    // Open previous
    activeSlideIndex.value--;
    expandedPackages.value[packages[activeSlideIndex.value].id] = true;
  }
};

const closeModal = () => {
  if (isPaywallForced.value) {
    return;
  }

  sendEventToBackend('paywall_dismiss', {
    crm_user_id: authStore.user?.id,
    email: authStore.user?.email,
    phone: authStore.user?.phone,
    fname: authStore.user?.firstName,
    lname: authStore.user?.lastName,
  });
  modelValue.value = false;
};

const { lock, unlock } = useBodyScrollLock();
const { isMobile } = useAppBreakpoints();

const isSafari = ref(false);

const scrollContainerClasses = computed(() => {
  return [
    'relative',
    'flex-1',
    'overflow-y-auto',
    'overflow-x-hidden',
    'min-h-0',
    'upgrade-modal__scroll-container',
    {
      'h-screen': isMobile,
      'max-h-screen': isMobile,
    },
  ];
});

watch(modelValue, (isOpen) => {
  if (isOpen) {
    lock();
  } else {
    unlock();
  }
});

const handleBackdropClick = (e: MouseEvent) => {
  if (isPaywallForced.value) {
    return;
  }
  if (e.target === e.currentTarget) {
    closeModal();
  }
};

const getTruncatedText = (text: string): string => {
  if (!text) return '';
  const words = text.split(' ');
  if (words.length <= 4) return text;
  return words.slice(0, 5).join(' ');
};

const avatarImages = [
  '4aac5e459977705a395e4b83b82d8a4e2087e983.webp',
  '67ffd9ab33f677397c671e58bef352ebc410bf03.webp',
  'e4f78ef96c510bd2c1e7b557b8dc18ce803d8bd4.webp',
  '54f6cd1c36fb4e587df08ad11e0f027158b1a4cc.webp',
];

const getAvatarPath = (index: number): string => {
  return `/avatars/${avatarImages[index - 1]}`;
};

const getTierColors = (tier: string) => {
  const pkg = allPackages.value.find((p) => p.tier === tier);

  if (pkg?.badgeColor) {
    return {
      primary: pkg.badgeColor,
      secondary: pkg.badgeColor,
      border: pkg.badgeColor,
    };
  }

  const tierLower = tier.toLowerCase();
  const defaultColors: Record<string, { primary: string; secondary: string; border: string }> = {
    scout: { primary: '#A1A1A1', secondary: '#696969', border: '#A1A1A1' },
    investor: { primary: '#CBA461', secondary: '#AB843E', border: '#CBA461' },
    syndicate: { primary: '#3E3D3D', secondary: '#161414', border: '#3E3D3D' },
  };

  return defaultColors[tierLower] || { primary: '#CBA461', secondary: '#AB843E', border: '#CBA461' };
};

const getHeaderGradient = (tier: string): string => {
  const colors = getTierColors(tier);
  return `background: linear-gradient(92.03deg, ${colors.primary} 1.68%, ${colors.secondary} 102.79%); color: white;`;
};

const getTierBorderColor = (tier: string): string => {
  return getTierColors(tier).border;
};

const getTierDisplayName = (tier: string): string => {
  const pkg = allPackages.value.find((p) => p.tier === tier);
  if (pkg?.name) {
    return pkg.name.replace(/\s*(monthly|yearly|lifetime)\s*/gi, '').trim();
  }
  return tierTabLabel(tier);
};

const getFeatureIconColor = (tier: string): string => {
  return getTierColors(tier).primary;
};

const parseFeature = (feature: string, index: number, featureDescriptions?: Record<string, string>) => {
  const text = typeof feature === 'string' ? feature : '';
  const trimmed = text.trim();
  const isPlaceholder = trimmed === '' || trimmed === '-';

  const name = isPlaceholder ? '' : text;
  const description = isPlaceholder ? '' : featureDescriptions?.[text] || '';

  return {
    key: `feature-${index}`,
    subtitle: '',
    name,
    description,
    hasInfo: !!description,
    isPlaceholder,
  };
};

const normalizeFeatures = (rawFeatures: unknown): string[] => {
  const list = Array.isArray(rawFeatures) ? rawFeatures : [];
  const actualFeatures = list.map((f) => (typeof f === 'string' ? f.trim() : '')).filter((f) => f !== '');

  if (actualFeatures.length === 0) {
    return Array(FEATURE_ROWS).fill('-');
  }

  if (actualFeatures.length >= FEATURE_ROWS) {
    return actualFeatures.slice(0, FEATURE_ROWS);
  }

  const paddingCount = FEATURE_ROWS - actualFeatures.length;
  const paddedFeatures = [
    ...actualFeatures.slice(0, -1),
    ...Array(paddingCount).fill('-'),
    actualFeatures[actualFeatures.length - 1],
  ];

  return paddedFeatures;
};

type ParsedFeature = {
  key: string;
  subtitle: string;
  name: string;
  description: string;
  hasInfo: boolean;
  isPlaceholder: boolean;
};

type PackageWithTier = Omit<Package, 'features'> & {
  features: ParsedFeature[];
};

const packages = computed((): PackageWithTier[] => {
  if (!allPackages.value || !Array.isArray(allPackages.value)) return [];

  try {
    const mappedPackages = allPackages.value
      .filter((pkg: Package) => pkg && pkg.tier && pkg.billingPeriod && pkg.billingPeriod === activeBillingPeriod.value)
      .map(
        (pkg: Package): PackageWithTier => ({
          ...pkg,
          features: normalizeFeatures(pkg.features).map((f, i) => parseFeature(f || '', i, pkg.featureDescriptions)),
        }),
      );

    const tierOrderFromTiers = packageTiers.value.map((t) => t.value);

    return mappedPackages.sort((a, b) => {
      const indexA = tierOrderFromTiers.indexOf(a.tier || '');
      const indexB = tierOrderFromTiers.indexOf(b.tier || '');

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  } catch (error) {
    console.error('Error processing packages:', error);
    return [];
  }
});

const filteredPackages = computed(() => {
  return packages.value;
});

const selectedPackage = computed(() => {
  const list = filteredPackages.value;
  if (!list.length) return null;
  if (selectedPackageId.value) {
    const found = list.find((p) => p.id === selectedPackageId.value);
    if (found) return found;
  }
  return list[0];
});

watch(
  filteredPackages,
  (list) => {
    if (!list.length) return;
    const currentInList = list.some((p) => p.id === selectedPackageId.value);
    if (!selectedPackageId.value || !currentInList) selectedPackageId.value = list[0].id;
  },
  { immediate: true },
);

const getPriceForPeriod = (pkg: PackageWithTier): string => {
  return pkg.price.toString();
};

// Raw (untranslated) period unit — used only for the payload sent to the
// payment popup, which may key off this value; kept in English on purpose.
const getPeriodLabel = (): string => {
  const periodLower = activeBillingPeriod.value?.toLowerCase();
  if (periodLower === 'lifetime') return 'lifetime';
  return 'mo';
};

// Display-only translated counterpart of getPeriodLabel(), for the "/mo" /
// "/lifetime" price suffix rendered in this component's own template.
const periodLabelDisplay = computed(() => {
  const periodLower = activeBillingPeriod.value?.toLowerCase();
  if (periodLower === 'lifetime') return t('learn.upgradeModal.periodLifetimeShort');
  return t('learn.upgradeModal.periodMonthShort');
});

const TIER_CTA_LABEL_KEYS: Record<string, string> = {
  scout: 'learn.upgradeModal.ctaStartScouting',
  investor: 'learn.upgradeModal.ctaStartInvesting',
  syndicate: 'learn.upgradeModal.ctaJoinSyndicate',
  default: 'learn.exclusiveModal.upgradeNow',
};

const getCtaButtonText = (pkg: PackageWithTier): string => {
  const tier = pkg.tier?.toLowerCase();
  const key = isValidTier(tier) && tier ? tier : 'default';
  return t(TIER_CTA_LABEL_KEYS[key] ?? TIER_CTA_LABEL_KEYS.default);
};

const getPrimaryCtaText = (pkg: PackageWithTier): string => {
  if (canStartTrial.value) {
    return t('learn.upgradeModal.startTrial', { days: freeTrialDays.value });
  }
  return getCtaButtonText(pkg);
};

const isLastTier = (tier: string | null | undefined): boolean => {
  if (!tier) return false;
  const lastTier = packageTiers.value[packageTiers.value.length - 1];
  return tier === lastTier?.value;
};

const uiStore = useUIStore();
const loadingPackageId = ref<string | null>(null);

// Free trial logic
const freeTrialDays = computed(() => {
  const raw = config.public?.freeTrialDays || config.freeTrialDays || 7;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : 7;
});

const isSubscriptionBilling = computed(() => {
  const period = activeBillingPeriod.value?.toLowerCase();
  return period === 'monthly' || period === 'yearly';
});

const canStartTrial = computed(() => {
  if (!isSubscriptionBilling.value) return false;
  const query = route.query;
  const freeTrialParam = query?.free_trial;
  let freeTrialStr: string | null | undefined = Array.isArray(freeTrialParam) ? freeTrialParam[0] : freeTrialParam;
  if ((freeTrialStr === undefined || freeTrialStr === null) && authStore.user?.utmParams) {
    try {
      const params = new URLSearchParams(authStore.user.utmParams);
      freeTrialStr = params.get('free_trial');
    } catch {
      freeTrialStr = null;
    }
  }
  const hasFreeTrialParam = String(freeTrialStr) === 'true' || String(freeTrialStr) === '1';
  const user = authStore.user;
  const hasNotClaimedTrial = !user?.trialClaimedAt;
  return hasFreeTrialParam && hasNotClaimedTrial;
});

const handleStartTrial = (pkg: PackageWithTier) => {
  if (!pkg?.id) {
    uiStore.showSnackBanner('error', t('learn.upgradeModal.packageNotFound'));
    return;
  }

  if (!pkg.stripeProductId) {
    uiStore.showSnackBanner('info', t('learn.upgradeModal.stripeNotConfigured'));
    return;
  }

  sendEventToBackend('trial_checkout_start', {
    crm_user_id: authStore.user?.id,
    email: authStore.user?.email,
    phone: authStore.user?.phone,
    fname: authStore.user?.firstName,
    lname: authStore.user?.lastName,
    package_id: pkg.id,
    amount: pkg.price,
    currency: 'USD',
    trial_days: freeTrialDays.value,
  });

  modelValue.value = false;

  setTimeout(() => {
    uiStore.showPopup(POPUPS.PAYMENT_START, {
      packageId: pkg.id,
      packageName: t('learn.upgradeModal.planName', { tier: getTierDisplayName(pkg.tier || '') }),
      packagePrice: 0, // $0 during trial
      packagePeriod: getPeriodLabel(),
      stripeProductId: pkg.stripeProductId,
      paypalPlanId: pkg.paypalPlanId,
      isTrial: true, // Pass trial flag to payment modal
    });
  }, 250);
};

const handlePrimaryCta = (pkg: PackageWithTier) => {
  if (canStartTrial.value) {
    handleStartTrial(pkg);
    return;
  }
  handleUpgrade(pkg);
};

const handleUpgrade = async (pkg: PackageWithTier) => {
  if (!pkg) {
    uiStore.showSnackBanner('error', t('learn.upgradeModal.packageNotFound'));
    return;
  }

  if (!pkg.stripeProductId) {
    uiStore.showSnackBanner('info', t('learn.upgradeModal.stripeNotConfigured'));
    return;
  }

  sendEventToBackend('checkout_start', {
    crm_user_id: authStore.user?.id,
    email: authStore.user?.email,
    phone: authStore.user?.phone,
    fname: authStore.user?.firstName,
    lname: authStore.user?.lastName,
    package_id: pkg.id,
    amount: pkg.price,
    currency: 'USD',
    is_recurring: pkg.billingPeriod === 'monthly' || pkg.billingPeriod === 'yearly',
  });

  modelValue.value = false;

  setTimeout(() => {
    uiStore.showPopup(POPUPS.PAYMENT_START, {
      packageId: pkg.id,
      packageName: t('learn.upgradeModal.planName', { tier: getTierDisplayName(pkg.tier || '') }),
      packagePrice: pkg.price,
      packagePeriod: getPeriodLabel(),
      stripeProductId: pkg.stripeProductId,
      paypalPlanId: pkg.paypalPlanId,
    });
  }, 250);
};

const handleBecomeMember = async () => {
  const mostPopularPackage = packages.value.find(
    (pkg) => packageTiers.value.find((t) => t.value === pkg.tier)?.badge === true,
  );

  if (mostPopularPackage) {
    await handleUpgrade(mostPopularPackage);
  } else if (packages.value.length > 0) {
    await handleUpgrade(packages.value[0]);
  }
};

const handleKey = (e: KeyboardEvent) => {
  if (isPaywallForced.value) {
    return;
  }
  if (e.key === 'Escape') closeModal();
};

onMounted(() => {
  if (typeof window === 'undefined') return;

  isSafari.value = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  windowWidth.value = window.innerWidth;

  window.addEventListener('keydown', handleKey);
  window.addEventListener('resize', handleResize);

  if (modelValue.value) {
    lock();
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', handleKey);
    window.removeEventListener('resize', handleResize);
  }
  unlock();
});
</script>

<style scoped>
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fade-move,
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* RTL: forward motion runs right-to-left. */
[dir='rtl'] .fade-enter-from {
  transform: translateX(-30px);
}

[dir='rtl'] .fade-leave-to {
  transform: translateX(30px);
}

.fade-leave-active {
  position: absolute;
}

.upgrade-modal__scroll-container {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
  scrollbar-width: thin;
  scrollbar-color: rgba(237, 164, 54, 0.3) transparent;
}

.upgrade-modal__scroll-container::-webkit-scrollbar {
  width: 6px;
}

.upgrade-modal__scroll-container::-webkit-scrollbar-track {
  background: transparent;
}

.upgrade-modal__scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(237, 164, 54, 0.3);
  border-radius: 3px;
}

.upgrade-modal__scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(237, 164, 54, 0.5);
}
</style>
